import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Inactivity tracking for the 30-minute idle sign-out (platform sessions and
 * the partner portal alike).
 *
 * "Activity" means the person touching the page — pointer, keyboard, scroll,
 * touch — never background requests, so a tab left open with polling still
 * goes idle. The last-activity time lives in localStorage under `storageKey`,
 * so activity in any tab of the site keeps every tab alive and they all time
 * out together.
 *
 * While the person is active, `onPing` is called at most every `pingEveryMs`
 * to tell the server (which enforces the same timeout and only counts pings).
 * `warningMs` before the end the hook reports a countdown (the callers use
 * IDLE_WARNING_MS, 5 minutes); at the end it calls `onIdle` once.
 */

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'scroll', 'touchstart'];
const WRITE_THROTTLE_MS = 5_000;
const TICK_MS = 1_000;

export interface IdleTimeoutOptions {
  enabled: boolean;
  timeoutMs: number;
  warningMs?: number;
  pingEveryMs?: number;
  storageKey: string;
  onIdle: () => void;
  onPing?: () => Promise<unknown> | void;
}

export interface IdleTimeoutState {
  /** Seconds until sign-out while the warning is due; null otherwise. */
  secondsLeft: number | null;
  /** Count as activity now (e.g. the "Stay signed in" button) and ping. */
  stayActive: () => void;
}

export function readLastActivity(storageKey: string, fallback: number): number {
  try {
    const value = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(value) && value > 0 ? value : fallback;
  } catch {
    return fallback;
  }
}

export function writeLastActivity(storageKey: string, at: number) {
  try {
    window.localStorage.setItem(storageKey, String(at));
  } catch {
    /* storage blocked: this tab still tracks activity in memory */
  }
}

/** "4:59", "0:07" — the countdown shown in the idle warning. */
export function formatCountdown(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** What the timer should do `idleForMs` after the last activity. */
export function idlePhase(
  idleForMs: number,
  timeoutMs: number,
  warningMs: number
): { kind: 'active' } | { kind: 'warning'; secondsLeft: number } | { kind: 'idle' } {
  if (idleForMs >= timeoutMs) return { kind: 'idle' };
  if (idleForMs >= timeoutMs - warningMs) {
    return { kind: 'warning', secondsLeft: Math.max(1, Math.ceil((timeoutMs - idleForMs) / 1000)) };
  }
  return { kind: 'active' };
}

export default function useIdleTimeout({
  enabled,
  timeoutMs,
  warningMs = 60_000,
  pingEveryMs = 60_000,
  storageKey,
  onIdle,
  onPing,
}: IdleTimeoutOptions): IdleTimeoutState {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const callbacks = useRef({ onIdle, onPing });
  callbacks.current = { onIdle, onPing };

  // In-memory copy: the source of truth when storage is unavailable.
  const lastActivity = useRef(Date.now());
  const lastWrite = useRef(0);
  const lastPing = useRef(0);
  const fired = useRef(false);

  const ping = useCallback(() => {
    lastPing.current = Date.now();
    try {
      const result = callbacks.current.onPing?.();
      if (result && typeof (result as Promise<unknown>).catch === 'function') {
        (result as Promise<unknown>).catch(() => undefined);
      }
    } catch {
      /* a failed ping is retried on the next active minute */
    }
  }, []);

  const markActive = useCallback(
    (force = false) => {
      const now = Date.now();
      lastActivity.current = now;
      if (force || now - lastWrite.current >= WRITE_THROTTLE_MS) {
        lastWrite.current = now;
        writeLastActivity(storageKey, now);
      }
    },
    [storageKey]
  );

  const tick = useCallback(() => {
    if (fired.current) return;
    const now = Date.now();
    const last = Math.max(lastActivity.current, readLastActivity(storageKey, 0));
    lastActivity.current = last;

    const phase = idlePhase(now - last, timeoutMs, warningMs);
    if (phase.kind === 'idle') {
      fired.current = true;
      setSecondsLeft(null);
      callbacks.current.onIdle();
      return;
    }
    setSecondsLeft(phase.kind === 'warning' ? phase.secondsLeft : null);

    // Tell the server only about real activity since the last ping.
    if (last > lastPing.current && now - lastPing.current >= pingEveryMs) ping();
  }, [storageKey, timeoutMs, warningMs, pingEveryMs, ping]);

  useEffect(() => {
    if (!enabled) {
      setSecondsLeft(null);
      return undefined;
    }
    fired.current = false;
    // Signing in (or arriving signed in) counts as activity.
    markActive(true);
    lastPing.current = Date.now();

    const onActivity = () => {
      // Any activity counts, including during the warning (moving towards the
      // dialog dismisses it) — but nothing revives a session already ended.
      if (!fired.current) markActive();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, { passive: true, capture: true })
    );
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(tick, TICK_MS);
    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity, { capture: true })
      );
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, markActive, tick]);

  const stayActive = useCallback(() => {
    markActive(true);
    setSecondsLeft(null);
    ping();
  }, [markActive, ping]);

  return { secondsLeft, stayActive };
}
